/* eslint-disable fp/no-mutation */
import React, { useReducer } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { WizardState } from './types'
import { STEPS } from './constants'
import ImageUploadStep from './image-upload-step'
import BackgroundConfigStep from './background-config-step'
import TextCustomizationStep from './text-customization-step'

let wizardReducer = (state: WizardState, action: any): WizardState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload }
    case 'SET_IMAGE':
      return { ...state, uploadedImage: action.payload }
    case 'SET_OPACITY':
      return { ...state, backgroundOpacity: action.payload }
    case 'SET_FLATTENED_BACKGROUND':
      return { ...state, flattenedBackground: action.payload }
    case 'ADD_TEXT':
      return {
        ...state,
        textElements: [...state.textElements, action.payload],
        selectedTextId: action.payload.id,
        selectedImageId: null,
      }
    case 'UPDATE_TEXT':
      return {
        ...state,
        textElements: state.textElements.map((el) =>
          el.id === action.payload.id
            ? { ...el, ...action.payload.updates }
            : el,
        ),
      }
    case 'DELETE_TEXT':
      return {
        ...state,
        textElements: state.textElements.filter(
          (el) => el.id !== action.payload,
        ),
        selectedTextId:
          state.selectedTextId === action.payload ? null : state.selectedTextId,
      }
    case 'SELECT_TEXT':
      return { ...state, selectedTextId: action.payload, selectedImageId: null }
    case 'ADD_IMAGE':
      return {
        ...state,
        imageElements: [...state.imageElements, action.payload],
        selectedImageId: action.payload.id,
        selectedTextId: null,
      }
    case 'UPDATE_IMAGE':
      return {
        ...state,
        imageElements: state.imageElements.map((el) =>
          el.id === action.payload.id
            ? { ...el, ...action.payload.updates }
            : el,
        ),
      }
    case 'DELETE_IMAGE':
      return {
        ...state,
        imageElements: state.imageElements.filter(
          (el) => el.id !== action.payload,
        ),
        selectedImageId:
          state.selectedImageId === action.payload ? null : state.selectedImageId,
      }
    case 'SELECT_IMAGE':
      return { ...state, selectedImageId: action.payload, selectedTextId: null }
    default:
      return state
  }
}

export default ({
  onComplete,
  onCancel,
  linkedServiceId,
}: {
  onComplete: (imageBlob: Blob) => void
  onCancel: () => void
  linkedServiceId?: string
}) => {
  let [state, dispatch] = useReducer(wizardReducer, {
    currentStep: 0,
    uploadedImage: undefined,
    backgroundOpacity: 0.15,
    flattenedBackground: null,
    textElements: [],
    selectedTextId: null,
    imageElements: [],
    selectedImageId: null,
  })

  let nextStep = () => {
    if (state.currentStep < STEPS.length - 1) {
      dispatch({ type: 'SET_STEP', payload: state.currentStep + 1 })
    }
  }

  let prevStep = () => {
    if (state.currentStep > 0) {
      dispatch({ type: 'SET_STEP', payload: state.currentStep - 1 })
    }
  }

  let canProceed = () => {
    switch (state.currentStep) {
      case 0:
        return state.uploadedImage !== undefined
      case 1:
        return state.flattenedBackground !== null
      case 2:
        return true
      default:
        return false
    }
  }

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '95vw',
          height: '95vh',
          maxWidth: 'none',
          maxHeight: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Certificate Designer
        </Typography>
        <IconButton onClick={onCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Stepper activeStep={state.currentStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {state.currentStep === 0 && (
            <ImageUploadStep
              image={state.uploadedImage}
              onImageChange={(image) =>
                dispatch({ type: 'SET_IMAGE', payload: image })
              }
            />
          )}
          {state.currentStep === 1 && (
            <BackgroundConfigStep
              image={state.uploadedImage}
              opacity={state.backgroundOpacity}
              onOpacityChange={(opacity) =>
                dispatch({ type: 'SET_OPACITY', payload: opacity })
              }
              onGenerateFlattenedBackground={(dataUrl) =>
                dispatch({ type: 'SET_FLATTENED_BACKGROUND', payload: dataUrl })
              }
            />
          )}
          {state.currentStep === 2 && (
            <TextCustomizationStep
              flattenedBackground={state.flattenedBackground}
              textElements={state.textElements}
              selectedTextId={state.selectedTextId}
              imageElements={state.imageElements}
              selectedImageId={state.selectedImageId}
              onAddText={(text) =>
                dispatch({ type: 'ADD_TEXT', payload: text })
              }
              onUpdateText={(id, updates) =>
                dispatch({ type: 'UPDATE_TEXT', payload: { id, updates } })
              }
              onDeleteText={(id) =>
                dispatch({ type: 'DELETE_TEXT', payload: id })
              }
              onSelectText={(id) =>
                dispatch({ type: 'SELECT_TEXT', payload: id })
              }
              onAddImage={(image) =>
                dispatch({ type: 'ADD_IMAGE', payload: image })
              }
              onUpdateImage={(id, updates) =>
                dispatch({ type: 'UPDATE_IMAGE', payload: { id, updates } })
              }
              onDeleteImage={(id) =>
                dispatch({ type: 'DELETE_IMAGE', payload: id })
              }
              onSelectImage={(id) =>
                dispatch({ type: 'SELECT_IMAGE', payload: id })
              }
              onComplete={onComplete}
              linkedServiceId={linkedServiceId}
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={prevStep}
            disabled={state.currentStep === 0}
            sx={{ textTransform: 'none' }}
          >
            Back
          </Button>
          {state.currentStep < STEPS.length - 1 && (
            <Button
              variant="contained"
              onClick={nextStep}
              disabled={!canProceed()}
              sx={{ textTransform: 'none' }}
            >
              Next
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
